/**
 * Licence assignment builders shared by the Licence Management page and its bulk assign dialog.
 *
 * <p>Holds the row mapper, the licence display/label maps and the request builders the page uses to
 * create a user from a row and to change an existing user's licences. Bulk assign reuses these
 * unchanged so that a bulk run sends exactly what doing each row by hand would (APD-13993).</p>
 */
import { Key } from 'react'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import {
    CXRecordLanguage,
    CXScoreLanguage,
    LicenceManagementPageLanguage,
    ReportingLanguage,
    UsersByExtension
} from '../../../../../i18n'
import { DevicesResponse, SessionAPIResponse } from '../../../../NonReact/Types/Types'
import { IsVarEmpty, isVarNotEmpty, IsStringEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ALL_VALUE } from '../../../../React/Common/Constants/DefaultValues'
import { ReportingAccessLevels } from '../../../../NonReact/constants/Permissions'
import { AdminUserBody, JSONResponse } from '../../../../NonReact/common/Types/Types'
import { ApplicationUser } from '../../../../NonReact/common/Types/AddUser/AddUserTypes'
import { isEmailAddressValid } from '../../../../React/Common/Utils/ValidityUtils'
import { getCMEIDFromTelSys } from '../../../../React/Common/API/ItemGetters'
import { getSession } from '../../../../React/Common/API/CommonAPI'
import { userFormStateMapper } from '../../../../React/SelfProvisioning/Mappers/UserMapper'
import { UserDefaults } from '../../../../React/Common/Types'
import { IndividualTelItem } from '../../../../React/Common/Types/Types'
import { UserTypes } from '../../../../React/SelfProvisioning/Constants/Constants'
import { UserDefaultNames } from '../../../../React/UsersByExtension/Constants/Constants'
import { requiresBusinessDeviceScope } from '../../../../React/AddUser/Utils/requiresBusinessDeviceScope'
import userDefaults from '../../../../React/UsersByExtension/Config/UserDefaults.json'
import {
    LicenceManagementEntryType,
    LicenceManagementEntryType as LicenceManagementEntryTypeValue,
    LicenceManagementListItem,
    LicenceManagementListRow
} from '../../../../NonReact/Types/LicenceManagementPageTypes'
import { CXScoreLicences } from '../../../../NonReact/constants/CXScoreLicences'
import { isCxsScoreSupportedForUserType } from '../../../../NonReact/utils/CxsSubscriptionAccessUtils'
import { INTEGRATION_TYPE, IntegrationType } from '../../../../React/TelSysConfiguration/Constants/Constants'
import { COMMA } from '../../../../NonReact/constants/Separators'
import { CXRecordLicences } from '../../../../NonReact/constants/CXRecordLicences'
import {
    CxiDataEntitlement,
    CxIntegrationLicence
} from '../../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'

export type LicenceDraftValues = {
    readonly userLicence: string
    readonly cxiLicence: CxIntegrationLicence
    readonly cxiDataEntitlement: boolean
    readonly cxsLicence: CXScoreLicences
    readonly cxrLicence: CXRecordLicences
    readonly cxrRecordEnabled: boolean
    readonly cxsScoreEnabled: boolean
}

/** Current CX monitoring flags on a row's device, fetched when the licences popup opens. */
export type DeviceMonitoringFlags = {
    readonly cxsScoreEnabled: boolean
    readonly cxrEnabled: boolean
}

export const DEFAULT_PENDING_LICENCE_VALUES: LicenceDraftValues = {
    userLicence: ReportingAccessLevels.NONE,
    cxiLicence: CxIntegrationLicence.NONE,
    cxiDataEntitlement: false,
    cxsLicence: CXScoreLicences.NONE,
    cxrLicence: CXRecordLicences.NONE,
    cxrRecordEnabled: false,
    cxsScoreEnabled: false
}

export const USER_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    NONE: UsersByExtension.UsersByExtension_UserType_NONE,
    None: UsersByExtension.UsersByExtension_UserType_NONE,
    ESSENTIALS: UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
    Essentials: UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
    PROFESSIONAL: UsersByExtension.UsersByExtension_UserType_PROFESSIONAL,
    Professional: UsersByExtension.UsersByExtension_UserType_PROFESSIONAL,
    ENTERPRISE: UsersByExtension.UsersByExtension_UserType_ENTERPRISE,
    Enterprise: UsersByExtension.UsersByExtension_UserType_ENTERPRISE,
    CALL_CENTRE_AGENT: UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
    'Call Centre Agent': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
    'Call Center Agent': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
    CALL_CENTRE_SUPERVISOR: UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
    'Call Centre Supervisor': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
    'Call Center Supervisor': UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
    'Essentials (Extension)': LicenceManagementPageLanguage.LicenceManagementPage_EssentialsExtension
}

export const CXS_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [CXScoreLicences.NONE]: CXScoreLanguage.CX_Score_Licences_None,
    [CXScoreLicences.CXS_AGENT]: CXScoreLanguage.CX_Score_Licences_CXSAgent,
    [CXScoreLicences.CXS_MANAGER]: CXScoreLanguage.CX_Score_Licences_CXSManager,
    ScoringEnabled: LicenceManagementPageLanguage.LicenceManagementPage_Licence_ScoringEnabled
}

export const CXR_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [CXRecordLicences.NONE]: CXRecordLanguage.CX_Record_Licences_None,
    [CXRecordLicences.CXR_USER]: CXRecordLanguage.CX_Record_Licences_CXRUser,
    [CXRecordLicences.CXR_MANAGER]: CXRecordLanguage.CX_Record_Licences_CXRManager,
    RecordingEnabled: LicenceManagementPageLanguage.LicenceManagementPage_Licence_RecordingEnabled
}

export const CXT_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    TranscribeEnabled: LicenceManagementPageLanguage.LicenceManagementPage_Licence_TranscribeEnabled
}

export const CXI_LICENCE_LANGUAGE_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [CxIntegrationLicence.NONE]: UsersByExtension.UsersByExtension_CxiLicenceType_NONE,
    None: UsersByExtension.UsersByExtension_CxiLicenceType_NONE,
    [CxIntegrationLicence.SOLO]: UsersByExtension.UsersByExtension_CxiLicenceType_SOLO,
    Solo: UsersByExtension.UsersByExtension_CxiLicenceType_SOLO,
    [CxIntegrationLicence.TEAMS]: UsersByExtension.UsersByExtension_CxiLicenceType_TEAMS,
    Teams: UsersByExtension.UsersByExtension_CxiLicenceType_TEAMS,
    [CxIntegrationLicence.CXI]: UsersByExtension.UsersByExtension_CxiLicenceType_CX_INTEGRATION,
    'CX Integration': UsersByExtension.UsersByExtension_CxiLicenceType_CX_INTEGRATION
}

/**
 * Resolves a backend licence value to the same display text used by Users by Extension.
 *
 * @param value Raw licence value returned by the licence management API.
 * @param language Loaded language handler.
 * @param languageKeyByValue Map from backend value to the language key used by Users by Extension.
 * @param telSysType Telephony server type used for MST/WxC-specific CXA labels.
 * @returns Localised display label, or the raw value when no shared label exists.
 */
export const getSharedLicenceDisplayValue = (
    value: Definable<string>,
    language: CLanguageHandler,
    languageKeyByValue: Readonly<Record<string, string>>,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): string => {
    if (IsVarEmpty(value) || value === EMPTY_STRING) return EMPTY_STRING

    // Teams Business renames the four reporting licences (Historic User / Real-Time User /
    // Agent / Manager) — match the popup's dropdown labels so the column and popup agree.
    if (telSysType === TelSysTypes.MS_TEAMS && integrationType === INTEGRATION_TYPE.BUSINESS) {
        const businessKey: Definable<string> = TEAMS_BUSINESS_USER_LICENCE_LABEL_KEY_BY_VALUE[value]

        if (isVarNotEmpty(businessKey)) return language.get(businessKey)
    }

    const languageKey: Definable<string> = languageKeyByValue[value]

    if (IsVarEmpty(languageKey)) return value

    return language.getWithTelSysCheck(languageKey, telSysType)
}

/**
 * Teams Business display names for the reporting licences, keyed by the stored licence value.
 * Mirrors the popup's option labels (resolveReportingLevelLabel) so both render identically.
 */
export const TEAMS_BUSINESS_USER_LICENCE_LABEL_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    [ReportingAccessLevels.ESSENTIALS]: ReportingLanguage.AccessLevelHistoricUser,
    [ReportingAccessLevels.PROFESSIONAL]: ReportingLanguage.AccessLevelRealTimeUser,
    [ReportingAccessLevels.CALL_CENTRE_AGENT]: ReportingLanguage.AccessLevelAgent,
    [ReportingAccessLevels.CALL_CENTRE_SUPERVISOR]: ReportingLanguage.AccessLevelManager
}

export const isDisplayableLicenceValue = (value: Definable<string>): boolean =>
    !IsStringEmpty(value) &&
    value !== ReportingAccessLevels.NONE &&
    value !== CXScoreLicences.NONE &&
    value !== CXRecordLicences.NONE &&
    value !== CxIntegrationLicence.NONE

/**
 * Identifies BroadWorks device rows that do not yet have a user, where the UI should show the
 * licence that will be used by default when a user is created.
 *
 * @param item Raw licence management list item.
 * @param entryType Normalised row entry type.
 * @param telSysType Current telephony server type filter.
 * @returns True when the row should display the BroadWorks default user licence.
 */
export const shouldDisplayBroadWorksDefaultLicence = (
    item: LicenceManagementListItem,
    entryType: LicenceManagementEntryTypeValue,
    telSysType?: Nullable<number | string>
): boolean =>
    telSysType === TelSysTypes.BROADSOFT_BROADWORKS &&
    entryType === LicenceManagementEntryType.DEVICE &&
    IsStringEmpty(item.Username) &&
    IsStringEmpty(item.UserLicence)

/**
 * Normalises the raw user licence value used by the licence display and draft state.
 *
 * @param item Raw licence management list item.
 * @param entryType Normalised row entry type.
 * @param telSysType Current telephony server type filter.
 * @returns Existing licence, BroadWorks default, or the shared "None" value.
 */
export const getUserLicenceValue = (
    item: LicenceManagementListItem,
    entryType: LicenceManagementEntryTypeValue,
    telSysType?: Nullable<number | string>
): string => {
    if (shouldDisplayBroadWorksDefaultLicence(item, entryType, telSysType)) return ReportingAccessLevels.ESSENTIALS

    if (IsStringEmpty(item.UserLicence)) return ReportingAccessLevels.NONE

    return item.UserLicence
}

/** Separator the licences display value is built with; the cell splits on it to stack the lines. */
export const LICENCE_DISPLAY_SEPARATOR: string = `${COMMA} `

/**
 * Joins the CXI entitlements (CXI User / CXI Data) into a single licences line. Uses a slash so the
 * combined entry stays on one line — the cell only splits on {@link LICENCE_DISPLAY_SEPARATOR}.
 */
export const CXI_ENTITLEMENT_SEPARATOR: string = ' / '

export const buildLicenceDisplayValue = (
    licenceValues: LicenceDraftValues,
    language: CLanguageHandler,
    isCxiDataEnabled: boolean,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): string => {
    const userLicence: string = getSharedLicenceDisplayValue(
        licenceValues.userLicence,
        language,
        USER_LICENCE_LANGUAGE_KEY_BY_VALUE,
        telSysType,
        integrationType
    )
    const cxiLicence: string = getSharedLicenceDisplayValue(
        licenceValues.cxiLicence,
        language,
        CXI_LICENCE_LANGUAGE_KEY_BY_VALUE,
        telSysType
    )
    const cxsLicence: string = getSharedLicenceDisplayValue(
        licenceValues.cxsLicence,
        language,
        CXS_LICENCE_LANGUAGE_KEY_BY_VALUE
    )
    const cxrLicence: string = getSharedLicenceDisplayValue(
        licenceValues.cxrLicence,
        language,
        CXR_LICENCE_LANGUAGE_KEY_BY_VALUE
    )

    return [
        isDisplayableLicenceValue(licenceValues.userLicence) ? userLicence : EMPTY_STRING,
        buildCxiDisplayPart(licenceValues, cxiLicence, language, isCxiDataEnabled),
        buildCxsDisplayPart(licenceValues, cxsLicence, language),
        buildCxrDisplayPart(licenceValues, cxrLicence, language)
    ]
        .filter((s: string): boolean => s !== EMPTY_STRING)
        .join(LICENCE_DISPLAY_SEPARATOR)
}

/**
 * Resolves the CX Integration entry for the licences display, combining both entitlements on one
 * line: "CXI User / CXI Data" when both are held, otherwise whichever single one is held.
 *
 * <p>The generic CXI User entitlement is shown as "CXI User" (matching the popup toggle and the
 * value the backend returns after a save) rather than the legacy "CX Integration" label its stored
 * enum value ({@link CxIntegrationLicence.CXI}) maps to — so toggling CXI User on updates the row
 * immediately, before the save round-trip. Provider subtypes (Solo / Teams) keep their own names.</p>
 */
export const buildCxiDisplayPart = (
    licenceValues: LicenceDraftValues,
    cxiUserLabel: string,
    language: CLanguageHandler,
    isCxiDataEnabled: boolean
): string => {
    let cxiUserPart: string = cxiUserLabel

    if (!isDisplayableLicenceValue(licenceValues.cxiLicence)) {
        cxiUserPart = EMPTY_STRING
    } else if (licenceValues.cxiLicence === CxIntegrationLicence.CXI) {
        cxiUserPart = language.get(LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_CxiUser)
    }
    const cxiDataPart: string =
        licenceValues.cxiDataEntitlement && isCxiDataEnabled
            ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_LicencePopup_CxiData)
            : EMPTY_STRING

    return [cxiUserPart, cxiDataPart].filter((s: string): boolean => s !== EMPTY_STRING).join(CXI_ENTITLEMENT_SEPARATOR)
}

/**
 * Resolves the CXS entry for the licences display: "Scoring Enabled" for device-only monitoring
 * (no CXS licence), "CXS Manager + Scoring" when a manager has monitoring on, otherwise the plain
 * licence name.
 */
export const buildCxsDisplayPart = (
    licenceValues: LicenceDraftValues,
    cxsLicenceLabel: string,
    language: CLanguageHandler
): string => {
    if (licenceValues.cxsLicence === CXScoreLicences.NONE) {
        return licenceValues.cxsScoreEnabled
            ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_Licence_ScoringEnabled)
            : EMPTY_STRING
    }

    if (licenceValues.cxsLicence === CXScoreLicences.CXS_MANAGER && licenceValues.cxsScoreEnabled) {
        return language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_Licence_WithScoring,
            cxsLicenceLabel
        )
    }

    return isDisplayableLicenceValue(licenceValues.cxsLicence) ? cxsLicenceLabel : EMPTY_STRING
}

/**
 * Resolves the CXR entry for the licences display: "Recording Enabled" for device-only monitoring
 * (no CXR licence), "CXR Manager + Recording" when a manager has monitoring on, otherwise the
 * plain licence name.
 */
export const buildCxrDisplayPart = (
    licenceValues: LicenceDraftValues,
    cxrLicenceLabel: string,
    language: CLanguageHandler
): string => {
    if (licenceValues.cxrLicence === CXRecordLicences.NONE) {
        return licenceValues.cxrRecordEnabled
            ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_Licence_RecordingEnabled)
            : EMPTY_STRING
    }

    if (licenceValues.cxrLicence === CXRecordLicences.CXR_MANAGER && licenceValues.cxrRecordEnabled) {
        return language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_Licence_WithRecording,
            cxrLicenceLabel
        )
    }

    return isDisplayableLicenceValue(licenceValues.cxrLicence) ? cxrLicenceLabel : EMPTY_STRING
}

export const getRowLicenceValues = (row: LicenceManagementListRow): LicenceDraftValues => ({
    userLicence: row.UserLicenceValue,
    cxiLicence: row.CXILicenceValue as CxIntegrationLicence,
    cxiDataEntitlement: row.CXIDataEnabled ?? false,
    cxsLicence: row.CXSLicenceValue as CXScoreLicences,
    cxrLicence: row.CXRLicenceValue as CXRecordLicences,
    cxrRecordEnabled: row.CXRecordEnabled ?? false,
    cxsScoreEnabled: row.CXScoreEnabled ?? false
})

/**
 * Normalises the telsys IntegrationType to lower case. The stored value's casing varies across
 * provisioning paths ('Legacy' vs 'legacy'), and a case mismatch against {@link INTEGRATION_TYPE}
 * made the licences popup hide every section on Legacy Teams telsys.
 */
export const normaliseTelSysIntegrationType = (telSys: Nullable<IndividualTelItem>): Nullable<IndividualTelItem> =>
    telSys && isStringNotEmpty(telSys.IntegrationType)
        ? { ...telSys, IntegrationType: telSys.IntegrationType?.toLowerCase() as IntegrationType }
        : telSys

/**
 * Row licence values seeded with the device's current CX monitoring flags. The score flag lives on
 * the device only; the record flag comes from the user when one exists, otherwise from the device
 * (licence-less rows can still have device-level monitoring).
 */
export const seedRowLicenceValues = (
    row: LicenceManagementListRow,
    monitoring: Definable<DeviceMonitoringFlags>
): LicenceDraftValues => {
    const base: LicenceDraftValues =
        row.Licences === EMPTY_STRING ? DEFAULT_PENDING_LICENCE_VALUES : getRowLicenceValues(row)

    return {
        ...base,
        cxsScoreEnabled: monitoring?.cxsScoreEnabled ?? row.CXScoreEnabled ?? false,
        cxrRecordEnabled: isStringNotEmpty(row.Username)
            ? base.cxrRecordEnabled
            : monitoring?.cxrEnabled ?? row.CXREnabled ?? false
    }
}

export const getUserTypeForLicenceValue = (licenceValue: string): string => {
    if (licenceValue === ReportingAccessLevels.CXA_PREMIUM) return UserTypes.CALL_CENTRE_AGENT

    if (licenceValue === ReportingAccessLevels.CXA_ADVANCED) return UserTypes.CALL_CENTRE_SUPERVISOR

    if (licenceValue === ReportingAccessLevels.CALL_CENTRE_AGENT) return UserTypes.CALL_CENTRE_AGENT

    if (licenceValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR) return UserTypes.CALL_CENTRE_SUPERVISOR

    return (
        Object.values(UserTypes).find((userType: string): boolean => {
            const defaultsForUserType: Definable<{ readonly licenceLevel?: string }> = (
                userDefaults as Record<string, { readonly licenceLevel?: string }>
            )[userType]

            return defaultsForUserType?.licenceLevel === licenceValue
        }) ?? UserTypes.ESSENTIALS
    )
}

/**
 * Telsys-specific user defaults, keyed by `${userType}:${telSysType}`. Mirrors Users by Extension so
 * MS Teams and Webex tiers pick up their own defaults (e.g. MS Teams Standard Reports) instead of the
 * BroadWorks ones (Enhanced Call Center Reports) (APD-13902).
 */
const USER_DEFAULTS_KEY_BY_TELSYS_USER_TYPE: Readonly<Record<string, string>> = {
    [`${UserTypes.CALL_CENTRE_AGENT}:${TelSysTypes.MS_TEAMS}`]: UserDefaultNames.CXA_PREMIUM,
    [`${UserTypes.CALL_CENTRE_SUPERVISOR}:${TelSysTypes.MS_TEAMS}`]: UserDefaultNames.CXA_ADVANCED,
    [`${UserTypes.CALL_CENTRE_SUPERVISOR}:${TelSysTypes.WEBEX_CALLING}`]: UserDefaultNames.WXC_SUPERVISOR
}

/**
 * Resolves the {@link userDefaults} key for a user type on the given telsys type, falling back to the
 * user type itself when the telsys has no specific defaults.
 *
 * @param userType User type resolved from the reporting level.
 * @param telSysType Telephony server type of the row.
 * @returns The user defaults key to hand to the user mapper.
 */
export const getUserDefaultsKeyForTelSys = (userType: string, telSysType?: Nullable<number | string>): string =>
    USER_DEFAULTS_KEY_BY_TELSYS_USER_TYPE[`${userType}:${telSysType}`] ?? userType

/**
 * Determines whether the staged CX Score subscription needs an ACD agent endpoint.
 *
 * <p>CX Score Agent always scores the user's own agent, so the backend requires the user to resolve
 * to an agent (error 11414) whatever the CX Analytics tier is — including CXA None, where the tier
 * itself needs no agent and the shared user mapper therefore emits the device endpoint. The mapper
 * cannot correct this: it treats an agent flag on a non-agent tier as a downgrade and drops it, so
 * the endpoint is overridden at the call site (APD-13455).</p>
 *
 * @param cxsLicence Staged CX Score subscription for the row.
 * @param agentNumber The row's own agent number, if it has one.
 * @returns True when the submitted endpoint must be the row's agent rather than its device.
 */
export const requiresAgentEndpointForCxs = (
    cxsLicence: Definable<CXScoreLicences>,
    agentNumber: Definable<string>
): boolean => cxsLicence === CXScoreLicences.CXS_AGENT && isStringNotEmpty(agentNumber)

/**
 * Builds the ACD agent endpoint for a row, preserving the telsys/partition of the mapped endpoint.
 *
 * <p>The row's assigned agent is used when it has one. Otherwise the device number is sent as the
 * agent number: a matched agent shares its device's number, which is how the shared user mapper
 * emits the agent endpoint for the agent reporting tiers.</p>
 *
 * @param row Licence management row being submitted.
 * @param mappedEndpoint Endpoint produced by the shared user mapper.
 * @returns The same endpoint with the agent number set and the device number cleared.
 */
export const buildAgentEndpointForRow = (
    row: LicenceManagementListRow,
    mappedEndpoint: AdminUserBody['Endpoint']
): AdminUserBody['Endpoint'] => ({
    ...mappedEndpoint,
    DeviceNumber: null,
    AgentNumber: isStringNotEmpty(row.AgentNumber) ? row.AgentNumber : row.DeviceNumber
})

/**
 * Adds the row's device to an endpoint that has none, when monitoring is being turned on.
 *
 * <p>CX Record and CX Score are device-level flags, so a user whose endpoint is an ACD agent alone
 * has nowhere for them to live and the backend rejects the licence. The device behind the agent is
 * added instead of asking the operator to assign one by hand, and the agent is left in place —
 * enabling recording must never cost the user their agent endpoint (APD-13568).</p>
 *
 * <p>Only true when the row already has a real agent to point at. Without this, a row that has never
 * had an agent (a device-only user promoted to a tier that can hold an agent, e.g. Supervisor) had its
 * device number submitted as the agent number instead, an endpoint the backend cannot resolve to any
 * agent, leaving the user with neither a device nor an agent (APD-13764).</p>
 *
 * @param endpoint Endpoint the save would otherwise submit.
 * @param row Licence management row being submitted, carrying the resolved device number.
 * @param licenceValues Staged licence values for the row.
 * @returns The endpoint, with the row's device filled in where one is needed and available.
 */
export const withMonitoringDevice = (
    endpoint: AdminUserBody['Endpoint'],
    row: LicenceManagementListRow,
    licenceValues: LicenceDraftValues
): AdminUserBody['Endpoint'] => {
    if (isStringNotEmpty(endpoint.DeviceNumber) || isStringNotEmpty(endpoint.AgentNumber)) {
        // Endpoint already holds something -- only a monitoring toggle may add a device to a bare slot.
        const wantsMonitoring = licenceValues.cxsScoreEnabled || licenceValues.cxrRecordEnabled
        if (!wantsMonitoring || isStringNotEmpty(endpoint.DeviceNumber) || IsStringEmpty(row.DeviceNumber))
            return endpoint
        return { ...endpoint, DeviceNumber: row.DeviceNumber }
    }

    // Endpoint is empty outright so its the first time this tier has had one. Default it to whichever of the
    // row's own identifiers exists, device preferred, so it isn't left with nothing (APD-13764).
    if (isStringNotEmpty(row.DeviceNumber)) return { ...endpoint, DeviceNumber: row.DeviceNumber }
    if (isStringNotEmpty(row.AgentNumber)) return { ...endpoint, AgentNumber: row.AgentNumber }
    return endpoint
}

/**
 * Reconciles the endpoint against the reporting tier being saved.
 *
 * <p>The tier dictates what its endpoint may hold: a Historic User reports on a partition, a
 * Real-Time User on its own extension device, and an Agent on its own ACD agent. Carrying the
 * previous tier's endpoint over is rejected by the backend — 11279 for a device the tier cannot
 * hold, 11284 for an agent it cannot hold — and turning monitoring on could add a device to a tier
 * that forbids one. Manager and None accept either a device or an agent, so their endpoint stands
 * as it is (APD-13643).</p>
 *
 * @param userType Reporting tier being saved.
 * @param endpoint Endpoint the save would otherwise submit.
 * @param row Licence management row being submitted, carrying the resolved device number.
 * @param licenceValues Staged licence values for the row.
 * @returns The endpoint reconciled against the tier.
 */
export const endpointForTier = (
    userType: string,
    endpoint: AdminUserBody['Endpoint'],
    row: LicenceManagementListRow,
    licenceValues: LicenceDraftValues
): AdminUserBody['Endpoint'] => {
    // A Historic User reports on a partition, so neither a device nor an agent may be its endpoint.
    // Its scope still carries the row's device, so a monitoring licence has somewhere to live.
    if (userType === UserTypes.ESSENTIALS) {
        return { ...endpoint, DeviceNumber: null, AgentNumber: null }
    }

    // A Real-Time User reports on its own extension: the device is required and an agent is not
    // allowed. The row's device stands in when the endpoint carried only an agent, which is what
    // the backend would otherwise have to resolve for us.
    if (userType === UserTypes.PROFESSIONAL) {
        return {
            ...endpoint,
            DeviceNumber: isStringNotEmpty(endpoint.DeviceNumber) ? endpoint.DeviceNumber : row.DeviceNumber,
            AgentNumber: null
        }
    }

    // An Agent reports on its own ACD agent, a device is not allowed in the saved state, but the
    // backend itself resolves a submitted device into its matching agent when none is already
    // known (CReqRespUserBase.SetIDsForEndpoint), the mirror image of how it resolves a device for
    // an agent-only Real-Time User endpoint. So the row's device is submitted here when no agent is
    // already known, rather than guessed at on the frontend, and the backend either resolves it to
    // the matching agent or rejects the save with its own validation error when the device has none
    // at all. Without this, a device-only tier promoted straight to Agent fell through to the
    // generic rule below, which would have kept the device as the endpoint outright, an endpoint
    // this tier cannot hold (11284) (APD-13764).
    if (userType === UserTypes.CALL_CENTRE_AGENT) {
        return isStringNotEmpty(endpoint.AgentNumber)
            ? { ...endpoint, DeviceNumber: null }
            : {
                  ...endpoint,
                  AgentNumber: null,
                  DeviceNumber: isStringNotEmpty(endpoint.DeviceNumber) ? endpoint.DeviceNumber : row.DeviceNumber
              }
    }

    return withMonitoringDevice(endpoint, row, licenceValues)
}

/**
 * Resolves staged licence values to what the user API stores: call scoring is off on a reporting tier that
 * cannot score calls (Essentials, Professional, Enterprise), whatever the draft holds.
 *
 * <p>The backend rejects scoring on those tiers (error 11423), and a device can still carry the flag on from
 * the device API, which does not check the tier. Applying the rule when the body is built rather than in the
 * popup covers bulk assign too and stages nothing just for opening the popup. The endpoint, the pending display
 * and the saved device baseline of an update use the same values, so none of them acts on scoring the save
 * turns off (APD-13435).</p>
 *
 * @param licenceValues Staged licence values for the row.
 * @returns The licence values with call scoring resolved for the reporting tier.
 */
export const resolveSavedLicenceValues = (licenceValues: LicenceDraftValues): LicenceDraftValues => ({
    ...licenceValues,
    cxsScoreEnabled: licenceValues.cxsScoreEnabled && isCxsScoreSupportedForUserType(licenceValues.userLicence)
})

/**
 * Fields the licences popup owns, resolved from the staged draft. Call scoring is resolved for the
 * reporting tier by {@link resolveSavedLicenceValues}.
 *
 * @param licenceValues Staged licence values for the row.
 * @returns The scope fragment carrying only the four licence fields the popup can change.
 */
export const licenceScopeFragment = (
    licenceValues: LicenceDraftValues
): Pick<
    AdminUserBody['Scope'],
    | 'CXILicenseType'
    | 'CXIDataEntitlement'
    | 'CXScoreLicenseType'
    | 'CXScoreEnabled'
    | 'CXRLicenseType'
    | 'CXRecordEnabled'
> => ({
    CXILicenseType: licenceValues.cxiLicence.toUpperCase() as CxIntegrationLicence,
    CXIDataEntitlement: licenceValues.cxiDataEntitlement ? CxiDataEntitlement.CXI_DATA : CxiDataEntitlement.NONE,
    CXScoreLicenseType: licenceValues.cxsLicence,
    CXScoreEnabled: resolveSavedLicenceValues(licenceValues).cxsScoreEnabled,
    CXRLicenseType: licenceValues.cxrLicence,
    CXRecordEnabled: licenceValues.cxrRecordEnabled
})

/**
 * Builds the update body for a row that already has an application user, starting from the user's
 * stored record.
 *
 * <p>The popup owns four licence fields and nothing else, so everything the operator did not touch —
 * the endpoint, time zone, administrative rights, call-recording integration, report permissions — is
 * carried over verbatim. Rebuilding the body from tier defaults instead silently rewrote the rest of
 * the account: it dropped an ACD agent endpoint, reset the time zone and stripped an administrator's
 * rights on nothing more than a monitoring toggle (APD-13568).</p>
 *
 * @param existing Stored user record the update is applied to.
 * @param licenceValues Staged licence values for the row.
 * @param endpoint Endpoint to submit, when the staged licences require a specific one.
 * @param tierDefaults Tier-derived fields, supplied only when the reporting level changed.
 * @returns The body to submit for the user.
 */
export const buildLicenceUpdateBody = (
    existing: ApplicationUser,
    licenceValues: LicenceDraftValues,
    endpoint: Definable<AdminUserBody['Endpoint']>,
    tierDefaults: Definable<AdminUserBody>
): AdminUserBody => ({
    Username: existing.Username,
    EmailAddr: existing.EmailAddr,
    FullName: existing.FullName,
    DefaultLang: existing.DefaultLang,
    TimeZone: existing.TimeZone,
    LockOut: existing.LockOut,
    EnableMFA: existing.EnableMFA,
    SendWelcomeEmail: false,
    Advanced: existing.Advanced,
    Endpoint: endpoint ?? existing.Endpoint,
    Scope: {
        ...existing.Scope,
        ...licenceScopeFragment(licenceValues),
        // The staged reporting level is authoritative: it is seeded from the row, so it matches the
        // stored level when the operator did not change it.
        ReportingAccess: licenceValues.userLicence,
        // A changed reporting level re-derives the fields that level dictates; on an unchanged level
        // the stored values stand.
        ...(tierDefaults
            ? {
                  DeviceNumber: tierDefaults.Scope.DeviceNumber,
                  AgentNumber: tierDefaults.Scope.AgentNumber,
                  EnhancedMobileReporting: tierDefaults.Scope.EnhancedMobileReporting,
                  DefaultReportCollection: tierDefaults.Scope.DefaultReportCollection
              }
            : {})
    },
    // Report permissions follow the reporting level, so they are re-derived with it. Administrative
    // rights never do, and are always the user's own.
    Permissions: {
        ...existing.Permissions,
        NonAdmin: tierDefaults ? tierDefaults.Permissions.NonAdmin : existing.Permissions.NonAdmin
    }
})
/**
 * Returns the device's telephony username when it is a usable e-mail address.
 *
 * Teams supplies its user id, Webex Calling the device primary e-mail and BroadWorks the user id
 * ({@code IDOnTelSys}) in {@code TelSysUsername}; any of them can be an e-mail address. Numeric ids
 * (BroadWorks extensions, Panasonic, Siemens) fail the e-mail check. The page's row editor and the
 * bulk assign dialog both prefill from this, so they always agree on the e-mail for a device.
 *
 * @param item Licence management list item or row.
 * @returns The telephony username when it is a valid e-mail address, otherwise undefined.
 */
export const getTelSysEmail = (item: Pick<LicenceManagementListItem, 'TelSysUsername'>): Definable<string> =>
    isEmailAddressValid(item.TelSysUsername) ? item.TelSysUsername : undefined

/**
 * Maps a licence management list item to the row the page renders and the builders consume.
 *
 * @param item Raw licence management list item.
 * @param key Row key.
 * @param language Loaded language handler.
 * @param isCxiDataEnabled Whether the CXI Data feature flag is enabled.
 * @param telSysType Current telephony server type.
 * @param integrationType Current telsys integration type.
 * @returns The mapped row.
 */
export const mapListItemToRow = (
    item: LicenceManagementListItem,
    key: Key,
    language: CLanguageHandler,
    isCxiDataEnabled: boolean,
    telSysType?: Nullable<number | string>,
    integrationType?: Nullable<IntegrationType>
): LicenceManagementListRow => {
    const entryType: LicenceManagementEntryTypeValue = item.EntryType ?? LicenceManagementEntryType.DEVICE
    const userLicenceValue: string = getUserLicenceValue(item, entryType, telSysType)

    const userLicence: string = getSharedLicenceDisplayValue(
        userLicenceValue,
        language,
        USER_LICENCE_LANGUAGE_KEY_BY_VALUE,
        telSysType,
        integrationType
    )
    const cxiLicence: string = getSharedLicenceDisplayValue(
        item.CXILicence,
        language,
        CXI_LICENCE_LANGUAGE_KEY_BY_VALUE,
        telSysType
    )
    const cxsLicence: string = getSharedLicenceDisplayValue(
        item.CXSLicence,
        language,
        CXS_LICENCE_LANGUAGE_KEY_BY_VALUE
    )
    const cxrLicence: string = getSharedLicenceDisplayValue(
        item.CXRLicence,
        language,
        CXR_LICENCE_LANGUAGE_KEY_BY_VALUE
    )
    const cxiDataEntitlement: boolean =
        (item.CXIDataEntitlement ?? EMPTY_STRING).toUpperCase() === CxiDataEntitlement.CXI_DATA
    const licenceValues: LicenceDraftValues = {
        userLicence: userLicenceValue,
        cxiLicence: (IsStringEmpty(item.CXILicence)
            ? CxIntegrationLicence.NONE
            : item.CXILicence) as CxIntegrationLicence,
        cxiDataEntitlement,
        cxsLicence: (IsStringEmpty(item.CXSLicence) ? CXScoreLicences.NONE : item.CXSLicence) as CXScoreLicences,
        cxrLicence: (IsStringEmpty(item.CXRLicence) ? CXRecordLicences.NONE : item.CXRLicence) as CXRecordLicences,
        // The record flag lives on the device. The list API only sets the device flag
        // (CXREnabled) for a user row and leaves the user-scope CXRecordEnabled false, so combine
        // both to reflect the persisted state for user and licence-less rows alike.
        cxrRecordEnabled: (item.CXRecordEnabled ?? false) || (item.CXREnabled ?? false),
        cxsScoreEnabled: item.CXScoreEnabled ?? false
    }

    return {
        key,
        Description: item.Description ?? EMPTY_STRING,
        EntryType: entryType,
        EntryTypeDisplay:
            entryType === LicenceManagementEntryType.USER
                ? language.get(LicenceManagementPageLanguage.LicenceManagementPage_Type_User)
                : language.get(LicenceManagementPageLanguage.LicenceManagementPage_Type_Device),
        TelSysID: item.TelSysID ?? 0,
        TelSysDescription: item.TelSysDescription ?? EMPTY_STRING,
        PartIDInTelSys: item.PartIDInTelSys ?? ALL_VALUE,
        PartitionDescription: item.PartitionDescription ?? EMPTY_STRING,
        DeviceNumber: item.DeviceNumber ?? EMPTY_STRING,
        TelSysUsername: item.TelSysUsername ?? EMPTY_STRING,
        HasDevice: item.HasDevice ?? false,
        Username: item.Username ?? EMPTY_STRING,
        FullName: item.FullName ?? EMPTY_STRING,
        EmailAddr: item.EmailAddr ?? EMPTY_STRING,
        AgentNumber: item.AgentNumber ?? EMPTY_STRING,
        WelcomeEmail: item.WelcomeEmail ?? true,
        UserLicence: userLicence,
        UserLicenceValue: licenceValues.userLicence,
        CXILicence: cxiLicence,
        CXILicenceValue: licenceValues.cxiLicence,
        CXIDataEnabled: licenceValues.cxiDataEntitlement,
        CXSLicence: cxsLicence,
        CXSLicenceValue: licenceValues.cxsLicence,
        CXRLicence: cxrLicence,
        CXRLicenceValue: licenceValues.cxrLicence,
        CXRecordEnabled: licenceValues.cxrRecordEnabled,
        CXScoreEnabled: item.CXScoreEnabled ?? false,
        CXREnabled: item.CXREnabled ?? false,
        HasAgent: item.HasAgent ?? false,
        Licences: buildLicenceDisplayValue(licenceValues, language, isCxiDataEnabled, telSysType, integrationType),
        Status: item.Status ?? EMPTY_STRING,
        PendingDeletion: item.PendingDeletion ?? false
    }
}

/**
 * Resolves a row's partition ID as a number.
 *
 * @param row Licence management row.
 * @returns The partition ID.
 */
export const toPartitionID = (row: LicenceManagementListRow): number =>
    typeof row.PartIDInTelSys === 'number' ? row.PartIDInTelSys : parseInt(row.PartIDInTelSys, 10)

/**
 * Resolves the CME a row's telsys belongs to, falling back to the default CME.
 *
 * @param row Licence management row.
 * @returns The CME ID.
 */
const resolveRowCmeID = (row: LicenceManagementListRow): Promise<string> =>
    getCMEIDFromTelSys(row.TelSysID)
        .then((cmeID: string | undefined): string => (isVarNotEmpty(cmeID) ? cmeID : DEFAULT_CME_ID))
        .catch((): string => DEFAULT_CME_ID)

/**
 * Builds the tier-default body for a row, used to re-derive the fields a changed reporting level
 * dictates: scope shape, mobile reporting, default report collection and report permissions.
 *
 * <p>Mirrors the AddUser/UBE pattern, so a new reporting level resets the feature permissions it
 * does not allow. It is only consulted on a tier change — see {@link buildLicenceUpdateBody}.</p>
 *
 * @param row Licence management row being submitted.
 * @param licenceValues Staged licence values for the row.
 * @param userType User type resolved from the staged reporting level.
 * @param partitionID Partition the row belongs to.
 * @param isAgentTier Whether the staged tier is an agent tier with an agent available.
 * @param telSysType Telephony server type, which picks telsys-specific user defaults (APD-13902).
 * @returns The tier-default user body.
 */
export const buildTierDefaults = async (
    row: LicenceManagementListRow,
    licenceValues: LicenceDraftValues,
    userType: string,
    partitionID: number,
    isAgentTier: boolean,
    telSysType: Nullable<number | string>
): Promise<AdminUserBody> => {
    const cmeID: string = await resolveRowCmeID(row)

    return userFormStateMapper(
        userDefaults as unknown as UserDefaults,
        {
            email: row.EmailAddr,
            name: row.FullName,
            userType: getUserDefaultsKeyForTelSys(userType, telSysType),
            welcomeEmail: false,
            useEmailAsUsername: false,
            cxiLicence: licenceValues.cxiLicence
        },
        cmeID,
        row.TelSysID,
        partitionID,
        undefined,
        {
            TelSysUsername: row.TelSysUsername,
            Description: row.FullName,
            DeviceNumber: row.DeviceNumber
        } as DevicesResponse,
        isAgentTier,
        row.Username,
        row.Username,
        licenceValues.cxsLicence
    )
}

/**
 * Builds the create request for a row that has no application user yet.
 *
 * <p>No existing user — the username is derived from the device the same way UBE does:
 * TelSysUsername is telsys-type-specific (IDOnTelSys for Broadworks, TelSysUserID for Teams,
 * PrimaryEmail for Webex). When empty, the e-mail is used as the username. A session nonce is
 * fetched so the mapper can encrypt the generated password.</p>
 *
 * @param row Licence management row the user is created for.
 * @param email E-mail address for the new user.
 * @param licenceValues Staged licence values, when any were chosen.
 * @param welcomeEmail Whether the welcome e-mail is sent.
 * @param integrationType Current telsys integration type.
 * @param telSysType Current telephony server type, which picks telsys-specific user defaults.
 * @returns The body to POST to the user API.
 */
export const buildCreateUserBody = async (
    row: LicenceManagementListRow,
    email: string,
    licenceValues: Definable<LicenceDraftValues>,
    welcomeEmail: boolean,
    integrationType: Nullable<IntegrationType>,
    telSysType: Nullable<number | string>
): Promise<AdminUserBody> => {
    const derivedUsername: string = row.TelSysUsername
    const useEmailAsUsername: boolean = IsStringEmpty(derivedUsername)
    const resolvedCmeID: string = await resolveRowCmeID(row)
    const session: JSONResponse<SessionAPIResponse> = await getSession()
    const partitionID: number = toPartitionID(row)
    const userType: string = getUserTypeForLicenceValue(licenceValues?.userLicence ?? ReportingAccessLevels.ESSENTIALS)
    // Agent reporting tiers (Agent and Manager) must hold the ACD agent as their endpoint, not the
    // device. HasAgent means the row resolves to a matching agent (the matched agent shares the
    // device's number), so the mapper emits the agent endpoint from the device number. Without a
    // resolvable agent, keep the device.
    const isAgentTier: boolean =
        (userType === UserTypes.CALL_CENTRE_AGENT || userType === UserTypes.CALL_CENTRE_SUPERVISOR) && row.HasAgent

    const user: AdminUserBody = await userFormStateMapper(
        userDefaults as unknown as UserDefaults,
        {
            email,
            name: row.Description,
            userType: getUserDefaultsKeyForTelSys(userType, telSysType),
            welcomeEmail,
            useEmailAsUsername,
            cxiLicence: licenceValues?.cxiLicence ?? CxIntegrationLicence.NONE,
            cxiDataEntitlement: licenceValues?.cxiDataEntitlement ?? false
        },
        resolvedCmeID,
        row.TelSysID,
        partitionID,
        session?.Nonce,
        {
            TelSysUsername: derivedUsername,
            Description: row.Description,
            DeviceNumber: row.DeviceNumber,
            DeviceTypeID: 0
        },
        isAgentTier,
        undefined,
        undefined,
        licenceValues?.cxsLicence,
        // A tier that cannot score calls is created with scoring off, which the backend requires. Unlike an
        // update, a create does not clear the device's own scoring flag, so the page shows it unchanged (APD-13435).
        (licenceValues?.cxsScoreEnabled ?? row.CXScoreEnabled ?? false) && isCxsScoreSupportedForUserType(userType)
    )

    // A staged CXS Agent subscription needs the agent endpoint even when the CXA tier does not
    // (CXA None); the mapper only emits one for the agent tiers, so it is corrected here
    // (APD-13455). Only when the row has an agent to point at (APD-13764).
    const needsAgentEndpoint: boolean =
        !isAgentTier && requiresAgentEndpointForCxs(licenceValues?.cxsLicence, row.AgentNumber)

    // The monitoring flags and CXR licence travel with the user create — the device API is only for
    // rows without a user (APD-12994). Defaults come from the row's device state so existing flags
    // are not cleared. Business Real-Time users must be scoped to their own device, not "All".
    return {
        ...user,
        ...(needsAgentEndpoint ? { Endpoint: buildAgentEndpointForRow(row, user.Endpoint) } : {}),
        Scope: {
            ...user.Scope,
            ...(requiresBusinessDeviceScope(userType, integrationType) ? { DeviceNumber: row.DeviceNumber } : {}),
            CXRLicenseType: licenceValues?.cxrLicence ?? CXRecordLicences.NONE,
            CXRecordEnabled: licenceValues?.cxrRecordEnabled ?? row.CXREnabled ?? false
        }
    }
}

/**
 * Builds the update request that applies staged licence values to a row's existing user.
 *
 * <p>Tier-derived fields are only re-derived when the reporting level actually changed; a
 * monitoring or CXI toggle must leave the rest of the account alone (APD-13568).</p>
 *
 * @param row Licence management row being submitted.
 * @param existing The user's stored record.
 * @param draftValues Staged licence values for the row.
 * @param seededUserLicence The reporting level the row held before the change.
 * @param integrationType Current telsys integration type.
 * @param telSysType Current telephony server type, which picks telsys-specific user defaults.
 * @returns The body to PUT to the user API.
 */
export const buildExistingUserLicenceUpdate = async (
    row: LicenceManagementListRow,
    existing: ApplicationUser,
    draftValues: LicenceDraftValues,
    seededUserLicence: string,
    integrationType: Nullable<IntegrationType>,
    telSysType: Nullable<number | string>
): Promise<AdminUserBody> => {
    const partitionID: number = toPartitionID(row)
    const userType: string = getUserTypeForLicenceValue(draftValues.userLicence)
    const isAgentTier: boolean = userType === UserTypes.CALL_CENTRE_AGENT && isStringNotEmpty(row.AgentNumber)
    const hasTierChange: boolean = draftValues.userLicence !== seededUserLicence
    const tierDefaults: Definable<AdminUserBody> = hasTierChange
        ? await buildTierDefaults(row, draftValues, userType, partitionID, isAgentTier, telSysType)
        : undefined
    // Agent reporting tiers must hold the ACD agent as their endpoint, and so must a staged CXS
    // Agent subscription whatever the CXA tier is (APD-13455), but only when the row has an agent,
    // not invented from its device number (APD-13764). Otherwise the user's own endpoint stands,
    // whether that is a device or an agent.
    const needsAgentEndpoint: boolean =
        isAgentTier || requiresAgentEndpointForCxs(draftValues.cxsLicence, row.AgentNumber)
    const baseEndpoint: AdminUserBody['Endpoint'] = needsAgentEndpoint
        ? buildAgentEndpointForRow(row, {
              TelSysID: row.TelSysID,
              PartIDInTelSys: partitionID,
              DeviceNumber: null,
              AgentNumber: null
          })
        : existing.Endpoint
    // Turning monitoring on for an agent-only endpoint fills in the agent's device rather than
    // failing, keeping the agent (APD-13568). Skipped where the endpoint is deliberately the agent
    // alone, which CXS Agent requires (APD-13455). Scoring the save turns off does not count (APD-13435).
    const endpoint: AdminUserBody['Endpoint'] = needsAgentEndpoint
        ? baseEndpoint
        : endpointForTier(userType, baseEndpoint, row, resolveSavedLicenceValues(draftValues))
    const body: AdminUserBody = buildLicenceUpdateBody(existing, draftValues, endpoint, tierDefaults)

    // Rows with a user send the monitoring flags with the licence in a single user API call — the
    // device API is only for rows without a user (APD-12994).
    return {
        ...body,
        Scope: {
            ...body.Scope,
            // Business Real-Time users must be scoped to their own device, not "All".
            ...(hasTierChange && requiresBusinessDeviceScope(userType, integrationType)
                ? { DeviceNumber: row.DeviceNumber }
                : {})
        }
    }
}
